import React, { useState, useEffect } from 'react';
import fontPreloader, { FontPreloadOptions } from '../utils/fontPreloader';

interface FontLoadingIndicatorProps {
  showProgress?: boolean;
  className?: string;
}

export const FontLoadingIndicator: React.FC<FontLoadingIndicatorProps> = ({ 
  showProgress = false, 
  className = '' 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({ loaded: 0, total: 0, percentage: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if fonts are already loaded
    if (fontPreloader.areAllFontsLoaded()) {
      return;
    }

    let mounted = true;
    
    const startPreloading = async () => {
      if (!mounted) return;
      
      setIsLoading(true);
      setIsVisible(true);
      
      const options: FontPreloadOptions = {
        onProgress: (loaded, total) => {
          if (!mounted) return;
          const percentage = Math.round((loaded / total) * 100);
          setProgress({ loaded, total, percentage });
        },
        onComplete: () => {
          if (!mounted) return;
          setIsLoading(false);
          // Keep visible for a moment to show completion
          setTimeout(() => {
            if (mounted) {
              setIsVisible(false);
            }
          }, 1000);
        },
        onError: (fontName, error) => {
          console.warn(`Font loading error for ${fontName}:`, error);
        }
      };

      try {
        await fontPreloader.preloadAllFonts(options);
      } catch (error) {
        console.error('Font preloading failed:', error);
        if (mounted) {
          setIsLoading(false);
          setIsVisible(false);
        }
      }
    };

    // Start preloading after a small delay to not interfere with initial page load
    const timer = setTimeout(startPreloading, 1000);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, []);

  if (!isVisible || true) {
    return null;
  }

  return (
    <div 
      className={`font-loading-indicator ${className}`}
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        maxWidth: '200px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
      }}
    >
      {isLoading ? (
        <>
          <div 
            style={{
              width: '12px',
              height: '12px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          />
          <span>
            {showProgress 
              ? `Loading fonts... ${progress.percentage}%`
              : 'Loading fonts...'
            }
          </span>
        </>
      ) : (
        <>
          <span style={{ color: '#4CAF50' }}>✓</span>
          <span>Fonts ready!</span>
        </>
      )}
      
      {/* CSS animation for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Hook for components that need to know font loading status
export const useFontLoadingStatus = () => {
  const [isLoaded, setIsLoaded] = useState(fontPreloader.areAllFontsLoaded());
  const [progress, setProgress] = useState(fontPreloader.getProgress());

  useEffect(() => {
    if (isLoaded) {
      return;
    }

    const checkStatus = () => {
      setIsLoaded(fontPreloader.areAllFontsLoaded());
      setProgress(fontPreloader.getProgress());
    };

    // Check periodically during loading
    const interval = setInterval(checkStatus, 500);

    return () => clearInterval(interval);
  }, [isLoaded]);

  return { isLoaded, progress };
};